/**
 * EVO — API mijozi.
 *
 * ============================================================================
 * TOKENLARNI SAQLASH SIYOSATI — nima uchun aynan shunday
 * ============================================================================
 *
 * `accessToken` FAQAT XOTIRADA (shu moduldagi o'zgaruvchida) saqlanadi.
 * `localStorage` ga YOZILMAYDI.
 *
 * Sabab: XSS. Agar sahifaga begona skript tushsa (masalan buzilgan npm paket
 * yoki reklama skripti), u `localStorage.getItem('token')` ni bir qatorda o'qiy oladi
 * va serverga jo'natadi. Xotiradagi o'zgaruvchini o'qish uchun esa skript
 * aynan shu modulning yopilgan (closure) doirasiga kirishi kerak — bu ancha qiyin.
 *
 * Natijada XSS zarari cheklanadi: sahifa yangilanishi bilan access token yo'qoladi.
 *
 * `refreshToken` esa `localStorage` da — chunki sahifa yangilanganda ham
 * foydalanuvchi qaytadan parol kiritmasligi kerak. Bu murosа: refresh token
 * o'g'irlansa ham server tomonda sessiya bekor qilinadi (`sessions.revoked_at`),
 * uning xeshi bazada saqlanadi va bir martalik ishlatiladi.
 *
 * IDEAL YECHIM (Faza 2): refresh tokenni `httpOnly; Secure; SameSite=Strict`
 * cookie'da saqlash — u JS uchun umuman ko'rinmaydi.
 *
 * TODO(auth): httpOnly cookie'ga o'tish. Bu ARXITEKTURA qarori va u
 * BIR TOMONLAMA qilinmaydi — web o'zi hal qila oladigan ish emas:
 *
 *   1. `POST /auth/login` va `/auth/refresh` javob bilan birga
 *      `Set-Cookie: evo_rt=…; HttpOnly; Secure; SameSite=Strict; Path=/api/auth`
 *      yuborishi kerak, `refreshToken` ni javob tanasida bermasligi kerak;
 *   2. API va web BITTA domenda bo'lishi shart (`SameSite=Strict` cross-site
 *      cookie'ni yubormaydi) — bu deploy sxemasiga ta'sir qiladi;
 *   3. cookie avtomatik yuborilgani uchun CSRF himoyasi qo'shilishi shart
 *      (double-submit token yoki `Origin` tekshiruvi) — hozir kerak emas,
 *      chunki token qo'lda `Authorization` sarlavhasiga qo'yiladi.
 *
 * NIMA UCHUN HOZIR QILINMADI: uchala band ham `apps/api` va infratuzilma
 * tomonida. Web tomonda o'zgarish kichik (`REFRESH_TOKEN_KEY` va u bilan
 * ishlaydigan uchta funksiyani olib tashlash), lekin server cookie
 * qo'ymasidan oldin qilinsa — kirish butunlay ishlamay qoladi.
 *
 * Shu paytgacha mavjud murosа xavfni cheklaydi: access token XOTIRADA
 * (yangilanishda yo'qoladi), refresh token esa server tomonda bekor
 * qilinadi (`sessions.revoked_at`), xeshi saqlanadi va bir martalik.
 */
import { t } from './i18n';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const REFRESH_TOKEN_KEY = 'evo.refreshToken';

// ---------------------------------------------------------------------------
// Xatolik turi
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  readonly status: number;
  /** Server bergan mashina o'qiydigan kod (bo'lsa) — UI shunga qarab qaror qiladi. */
  readonly code: string | null;
  /** Validatsiya xatolari va boshqa qo'shimcha ma'lumot. */
  readonly details: unknown;
  /**
   * Xato javobining XOM tanasi.
   *
   * NIMA UCHUN KERAK: ba'zi xatolar shunchaki «nima bo'ldi» emas, «endi nima
   * qilish kerak» ni ham olib keladi. Eng aniq misol — mahsulot yaratishdagi
   * 422:
   *
   *   { code, message, candidates: [...], retryWith: 'force=true' }
   *
   * `candidates` ro'yxatini ko'rsatmasdan turib operatorga «dublikat bo'lishi
   * mumkin» deyish foydasiz: u nimani solishtirishni bilmaydi. Bu maydonlar
   * `message`/`code` ga sig'maydi va har xato uchun alohida tip ochish ham
   * ortiqcha, shuning uchun tana o'zgarishsiz saqlanadi — ekran o'zi kerakli
   * shaklga toraytiradi (`isRecord` + maydon tekshiruvi).
   */
  readonly body: unknown;

  constructor(
    status: number,
    message: string,
    code: string | null = null,
    details: unknown = null,
    body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.body = body;
  }

  /** Tarmoq uzilishi — server javob bermadi (GAP_ANALYSIS §C1: internet uziladi). */
  get isNetworkError(): boolean {
    return this.status === 0;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

// ---------------------------------------------------------------------------
// Token saqlash
// ---------------------------------------------------------------------------

/** XOTIRADA. localStorage'ga hech qachon yozilmaydi — fayl boshidagi izohga qarang. */
let accessToken: string | null = null;

/**
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ PARTNER TASHKILOTNI KO'RISH TOKENI — O'Z SESSIYANING USTIGA QO'YILADI    ║
 * ║                                                                          ║
 * ║ Egasining talabi: «biz uni kerak paytda uning organizatsiyasiga kirib    ║
 * ║ ko'ra olamiz». Server buning uchun ALOHIDA token beradi                  ║
 * ║ (`POST /auth/partner-view/:tenantId`): faqat `.view` bilan tugaydigan    ║
 * ║ ruxsatlar, 30 daqiqa, va REFRESH TOKENI YO'Q.                           ║
 * ║                                                                          ║
 * ║ NIMA UCHUN ALOHIDA O'ZGARUVCHI, `accessToken` NING USTIGA YOZILMAYDI:   ║
 * ║                                                                          ║
 * ║   1. Foydalanuvchi O'Z sessiyasiga QAYTISHI kerak. `accessToken` ni     ║
 * ║      almashtirsak, qaytish uchun yagona yo'l refresh bo'lardi — ya'ni    ║
 * ║      «Chiqish» tugmasi tarmoqqa bog'liq bo'lib qolardi va internet       ║
 * ║      uzilganda odam partner ekranida QAMALIB qolardi.                    ║
 * ║   2. `refreshToken` ga UMUMAN TEGILMAYDI. U localStorage'da o'z         ║
 * ║      egasiniki bo'lib qoladi; partner ko'rinishi uni na o'qiydi, na      ║
 * ║      yozadi.                                                             ║
 * ║                                                                          ║
 * ║ ENG MUHIMI — 401 KELGANDA REFRESH QILINMAYDI (`requestWithRefresh`      ║
 * ║ ichidagi izohga qarang). Bu shu yerdagi ajratishning butun sababi.       ║
 * ║                                                                          ║
 * ║ Token FAQAT XOTIRADA: sahifa yangilansa u yo'qoladi va foydalanuvchi     ║
 * ║ O'Z tashkilotida uyg'onadi. Bu yo'qotish emas, xavfsiz sukut holati —    ║
 * ║ saqlansa, jurnalga yozilmagan, muddati noma'lum tashrif tiklanardi,      ║
 * ║ har kirish esa ALOHIDA audit yozuvi bo'lishi kerak.                      ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
let partnerViewToken: string | null = null;

function readRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch {
    return null;
  }
}

function writeRefreshToken(token: string | null): void {
  try {
    if (token === null) {
      localStorage.removeItem(REFRESH_TOKEN_KEY);
    } else {
      localStorage.setItem(REFRESH_TOKEN_KEY, token);
    }
  } catch {
    // Private rejimda localStorage yozishni bloklaydi — sessiya faqat shu tabda yashaydi.
  }
}

export const tokenStore = {
  getAccessToken: (): string | null => accessToken,
  getRefreshToken: readRefreshToken,
  hasSession: (): boolean => accessToken !== null || readRefreshToken() !== null,

  set(tokens: { accessToken: string; refreshToken?: string | null }): void {
    accessToken = tokens.accessToken;
    if (tokens.refreshToken !== undefined && tokens.refreshToken !== null) {
      writeRefreshToken(tokens.refreshToken);
    }
  },

  /**
   * Partner ko'rinishini YOQADI yoki O'CHIRADI.
   *
   * `null` — o'z sessiyaga qaytish. Hech qanday so'rov ketmaydi: o'z
   * `accessToken` i joyida turgan edi va darhol yana ishlaydi.
   */
  setPartnerView(token: string | null): void {
    partnerViewToken = token;
  },

  getPartnerViewToken: (): string | null => partnerViewToken,

  clear(): void {
    accessToken = null;
    // Chiqishda partner ko'rinishi ham tugaydi — aks holda tizimdan
    // chiqqan foydalanuvchining brauzerida tirik token qolib ketardi.
    partnerViewToken = null;
    writeRefreshToken(null);
  },
};

/**
 * Sessiya tiklab bo'lmaganda chaqiriladi (refresh ham ishlamadi).
 * Auth konteksti shu yerga o'zining `logout` ini ulaydi — api.ts React'ni bilmaydi.
 */
type SessionExpiredHandler = () => void;
let onSessionExpired: SessionExpiredHandler | null = null;

export function setSessionExpiredHandler(handler: SessionExpiredHandler | null): void {
  onSessionExpired = handler;
}

/**
 * Partner ko'rinishi TUGAGANDA chaqiriladi (30 daqiqa o'tdi yoki server
 * tokenni rad etdi).
 *
 * `onSessionExpired` dan ALOHIDA va bu ataylab: u foydalanuvchini tizimdan
 * CHIQARADI (login ekrani), bu esa aksincha — foydalanuvchi hech qayerga
 * chiqmaydi, u O'Z tashkilotiga QAYTADI. Ikkalasini bitta handler bilan
 * qilish «muddat tugadi» ni «sessiyangiz tugadi» ga aylantirardi va odam
 * bekordan-bekorga qaytadan parol kiritardi.
 */
type PartnerViewExpiredHandler = () => void;
let onPartnerViewExpired: PartnerViewExpiredHandler | null = null;

export function setPartnerViewExpiredHandler(handler: PartnerViewExpiredHandler | null): void {
  onPartnerViewExpired = handler;
}

// ---------------------------------------------------------------------------
// Xato javobini o'qish
// ---------------------------------------------------------------------------

interface ErrorBody {
  message?: string | string[];
  error?: string;
  code?: string;
  statusCode?: number;
  details?: unknown;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ErrorBody | null = null;
  try {
    body = (await response.json()) as ErrorBody;
  } catch {
    // Javob JSON emas (proxy xatosi, HTML sahifa) — status bo'yicha xabar tuzamiz.
  }

  const rawMessage = body?.message;
  const message =
    (Array.isArray(rawMessage) ? rawMessage[0] : rawMessage) ??
    body?.error ??
    defaultMessageFor(response.status);

  return new ApiError(response.status, message, body?.code ?? null, body?.details ?? null, body);
}

/** `unknown` ni obyekt sifatida o'qish uchun tor qo'riqchi — xato tanasini tekshirishda ishlatiladi. */
export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Server qaytargan maydon xatolari: `{ errors: [{ path, message }] }`.
 *
 * API validatsiya xatolarini AYNAN shu shaklda qaytaradi (`ZodValidationPipe` va
 * `parseQueryOrThrow`), lekin ular `details` da emas, ildizda turadi — shuning
 * uchun `ApiError.details` bu holatda bo'sh bo'ladi va tanani o'qish kerak.
 * Natija maydon nomi → xabar: forma to'g'ridan-to'g'ri katak ostiga yozadi.
 */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof ApiError) || !isRecord(error.body)) return {};

  const raw = error.body.errors;
  if (!Array.isArray(raw)) return {};

  const result: Record<string, string> = {};
  for (const issue of raw) {
    if (isRecord(issue) && typeof issue.path === 'string' && typeof issue.message === 'string') {
      // Birinchi xato saqlanadi: bitta maydon ostida ikkita qizil qator ortiqcha.
      result[issue.path] ??= issue.message;
    }
  }
  return result;
}

function defaultMessageFor(status: number): string {
  if (status === 401) return t('auth.sessionExpired');
  if (status === 403) return t('error.403');
  if (status === 404) return t('error.404');
  if (status >= 500) return t('error.500');
  return t('common.unknownError');
}

// ---------------------------------------------------------------------------
// Refresh — bir vaqtda BITTA
// ---------------------------------------------------------------------------

interface RefreshResponse {
  readonly accessToken: string;
  readonly refreshToken?: string;
}

/**
 * Ayni damda ketayotgan refresh so'rovi.
 *
 * NIMA UCHUN BITTA: bosh sahifa ochilganda 5-6 ta so'rov parallel ketadi
 * (qoldiq, kurs, buyurtmalar, qarz...). Access token muddati tugagan bo'lsa,
 * ularning HAMMASI 401 qaytaradi. Har biri alohida refresh qilsa:
 *   - serverga 6 ta ortiqcha so'rov,
 *   - refresh token bir martalik (rotation) bo'lsa — 5 tasi bekor qilingan
 *     token bilan ketadi va foydalanuvchi tizimdan chiqib qoladi.
 *
 * Shuning uchun birinchi 401 refresh'ni boshlaydi, qolganlari SHU O'SHA
 * Promise'ni kutadi.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null;

/**
 * Refresh natijasi — UCH xil, ikki emas.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ NIMA UCHUN «TARMOQ» VA «RAD ETILDI» AJRATILADI                           ║
 * ║                                                                          ║
 * ║ Avval ikkalasi ham `null` qaytarardi va chaqiruvchi ikkalasida ham       ║
 * ║ bir xil ish qilardi: tokenlarni O'CHIRIB, foydalanuvchini tizimdan       ║
 * ║ CHIQARIB yuborardi.                                                      ║
 * ║                                                                          ║
 * ║ Natijada server bir soniyaga uzilsa (deploy, restart, internet           ║
 * ║ sekinlashuvi) foydalanuvchi tizimdan chiqib ketardi va qaytadan parol    ║
 * ║ kiritishga majbur bo'lardi — sessiyasi esa BUTUNLAY YARoqLI edi.         ║
 * ║                                                                          ║
 * ║ Eng yomoni: `refreshToken` `localStorage` dan O'CHIRILARDI, ya'ni        ║
 * ║ internet qaytganda ham sessiyani tiklab bo'lmasdi. Bir soniyalik         ║
 * ║ uzilish sessiyani BUTUNLAY yo'q qilardi.                                 ║
 * ║                                                                          ║
 * ║ `performRefresh` ning o'zida izoh to'g'ri yozilgan edi («tarmoq uzilgan  ║
 * ║ bo'lsa tokenlarni o'chirmaymiz»), lekin u FAQAT shu funksiya ichida      ║
 * ║ bajarilardi — chaqiruvchi baribir o'chirardi. Ya'ni niyat kodda bor      ║
 * ║ edi, natija esa yo'q.                                                    ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
export type RefreshOutcome =
  /** Yangi token olindi. */
  | { readonly kind: 'ok'; readonly accessToken: string }
  /** Server RAD ETDI — sessiya haqiqatan tugagan yoki bekor qilingan. */
  | { readonly kind: 'rejected' }
  /** Serverga yetib bo'lmadi — sessiya haqida HECH NARSA ma'lum emas. */
  | { readonly kind: 'network' };

async function performRefresh(): Promise<RefreshOutcome> {
  const refreshToken = readRefreshToken();
  // Token umuman yo'q — bu «rad etildi» bilan bir xil: tiklashga narsa yo'q.
  if (!refreshToken) return { kind: 'rejected' };

  let response: Response;
  try {
    response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Tarmoq uzilgan: tokenlarga TEGILMAYDI — internet qaytganda sessiya
    // davom etishi kerak (GAP_ANALYSIS §C1).
    return { kind: 'network' };
  }

  /*
   * 5xx ham TARMOQ holati.
   *
   * Server ko'tarilayotgan yoki qayta ishga tushayotgan paytda 502/503
   * qaytishi mumkin. Bu «sessiyang yaroqsiz» degani EMAS — server hali
   * javob bera olmayapti. Faqat 4xx (401/403) sessiya haqidagi javob.
   */
  if (response.status >= 500) return { kind: 'network' };

  if (!response.ok) {
    tokenStore.clear();
    return { kind: 'rejected' };
  }

  try {
    const data = (await response.json()) as RefreshResponse;
    tokenStore.set({ accessToken: data.accessToken, refreshToken: data.refreshToken ?? null });
    return { kind: 'ok', accessToken: data.accessToken };
  } catch {
    // 200 keldi, lekin tana buzilgan — bu server xatosi, sessiya emas.
    return { kind: 'network' };
  }
}

function refreshAccessToken(): Promise<RefreshOutcome> {
  refreshInFlight ??= performRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

// ---------------------------------------------------------------------------
// Asosiy so'rov funksiyasi
// ---------------------------------------------------------------------------

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** JSON tanasi. `FormData` kerak bo'lsa `rawBody` ishlatiladi. */
  readonly body?: unknown;
  readonly rawBody?: BodyInit;
  readonly query?: Readonly<Record<string, string | number | boolean | null | undefined>>;
  readonly headers?: Readonly<Record<string, string>>;
  readonly signal?: AbortSignal;
  /** `false` bo'lsa Authorization qo'shilmaydi (login, refresh). */
  readonly auth?: boolean;
}

function buildUrl(path: string, query: RequestOptions['query']): string {
  const url = `${BASE_URL}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== null && value !== undefined && value !== '') {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json', ...options.headers };

  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (token !== null) {
    headers.Authorization = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
  };
  if (options.signal) init.signal = options.signal;
  if (options.rawBody !== undefined) {
    init.body = options.rawBody;
  } else if (options.body !== undefined) {
    init.body = JSON.stringify(options.body);
  }

  return fetch(buildUrl(path, options.query), init);
}

/**
 * So'rov + sessiyani tiklash. Javob TALQIN QILINMAYDI.
 *
 * 401 kelganda: bitta umumiy refresh kutiladi, keyin so'rov BIR MARTA
 * qaytariladi. Ikkinchi 401 — sessiya haqiqatan tugagan.
 *
 * Bu qism `apiFetch()` dan AJRATILGAN, chunki server hamma joyda ham JSON
 * qaytarmaydi: `GET /api/print/*` tayyor HTML beradi. Ilgari butun mantiq
 * `apiFetch()` ichida edi va HTML olish uchun yagona yo'l uni `fetch()` bilan
 * qo'lda takrorlash bo'lardi — ya'ni tokenni tiklash, tarmoq xatosini
 * farqlash va sessiyani tozalash mantig'i IKKINCHI marta yozilardi. Ikki
 * nusxaning biri albatta eskirardi va u aynan xavfsizlik mantig'i.
 */
async function requestWithRefresh(path: string, options: RequestOptions): Promise<Response> {
  const useAuth = options.auth !== false;

  /*
   * Partner ko'rinishi YOQILGAN bo'lsa, so'rov O'SHA token bilan ketadi.
   *
   * Bir vaqtda ikkalasi yuborilmaydi — `Authorization` bitta va u eng
   * ustki kontekstga tegishli. Ya'ni partner ko'rinishida BARCHA so'rov
   * partnerning ma'lumotini oladi, «bir ekran meniki, ikkinchisi uniki»
   * degan aralashuv tuzilmaviy ravishda mumkin emas.
   */
  const outgoingToken = useAuth ? (partnerViewToken ?? accessToken) : null;

  let response: Response;
  try {
    response = await send(path, options, outgoingToken);
  } catch {
    throw new ApiError(0, t('common.networkError'));
  }

  if (response.status === 401 && useAuth) {
    /*
     * ╔════════════════════════════════════════════════════════════════════╗
     * ║ PARTNER TOKENI REFRESH YO'LIGA UMUMAN KIRMAYDI                     ║
     * ║                                                                    ║
     * ║ Unga refresh tokeni BERILMAGAN (server ataylab bermaydi: har       ║
     * ║ kirish alohida audit yozuvi bo'lishi kerak). Demak bu yerda        ║
     * ║ `refreshAccessToken()` ni chaqirish localStorage'dagi O'Z          ║
     * ║ refresh tokenimizni ishlatardi va natijada O'Z tashkilotimizning   ║
     * ║ yangi access tokeni kelardi.                                       ║
     * ║                                                                    ║
     * ║ Oqibati eng yomon turdagi xato bo'lardi: so'rov QAYTA yuboriladi,  ║
     * ║ 200 qaytadi, ekran to'ladi — lekin ekranda endi BIZNING ma'lumot   ║
     * ║ turadi, tepasida esa hamon «partner tashkilot ko'rinishi» degan    ║
     * ║ ogohlantiruvchi banner. Ya'ni foydalanuvchi o'z raqamlarini        ║
     * ║ partnerniki deb o'qiydi va bunga hech narsa ishora qilmaydi.       ║
     * ║                                                                    ║
     * ║ Shuning uchun bu yerda 401 BITTA ma'noni bildiradi: ko'rish oynasi ║
     * ║ tugadi. Token darhol o'chiriladi (o'lik tokenni qayta yuborishning ║
     * ║ ma'nosi yo'q), UI o'z sessiyaga qaytariladi va sabab aytiladi.     ║
     * ╚════════════════════════════════════════════════════════════════════╝
     */
    if (partnerViewToken !== null) {
      /*
       * Token SHU YERDA o'chirilMAYDI — uni ko'rinishdan chiqarayotgan UI
       * o'chiradi (`tokenStore.setPartnerView(null)`).
       *
       * Sabab parallel so'rovlarda: bosh sahifada bir vaqtda 5-6 so'rov
       * ketadi va muddat tugaganda HAMMASI 401 qaytaradi. Agar birinchisi
       * tokenni nolga tushirsa, ikkinchisi uchun bu shart YOLG'ON bo'lib
       * qoladi va u pastdagi REFRESH yo'liga tushardi — ya'ni aynan
       * yuqorida taqiqlangan narsa, faqat poyga (race) shaklida: gohida
       * ishlaydi, gohida bizning ma'lumotni partnerniki deb ko'rsatadi.
       *
       * Bayroq UI qo'lida turgani uchun barcha 401 lar BIR XIL javob
       * oladi. Handler ulanmagan holat (provider hali/endi yo'q) uchun esa
       * quyida zaxira bor: aks holda tirik dasturda o'lik token qolib,
       * har so'rov 401 beraverardi.
       */
      if (onPartnerViewExpired === null) partnerViewToken = null;
      onPartnerViewExpired?.();
      throw new ApiError(401, t('partnerView.expired'), 'PARTNER_VIEW_EXPIRED');
    }

    const outcome = await refreshAccessToken();

    /*
     * TARMOQ xatosi — foydalanuvchi tizimdan CHIQARILMAYDI.
     *
     * Sessiya haqida hech narsa ma'lum emas: server javob bermadi. Uni
     * «tugagan» deb hisoblash — bir soniyalik uzilish uchun odamni
     * qaytadan parol kiritishga majburlash, va `refreshToken` o'chgani
     * uchun internet qaytganda ham tiklab bo'lmaydi.
     *
     * Tarmoq xatosi qaytariladi: ekran «internet yo'q, qayta urinib
     * ko'ring» deydi va foydalanuvchi o'sha joyida qoladi.
     */
    if (outcome.kind === 'network') {
      throw new ApiError(0, t('common.networkError'));
    }

    if (outcome.kind === 'rejected') {
      tokenStore.clear();
      onSessionExpired?.();
      throw new ApiError(401, t('auth.sessionExpired'));
    }

    // Bir marta va faqat bir marta qayta urinish — sikl bo'lmasligi uchun.
    try {
      response = await send(path, options, outcome.accessToken);
    } catch {
      throw new ApiError(0, t('common.networkError'));
    }

    if (response.status === 401) {
      tokenStore.clear();
      onSessionExpired?.();
      throw new ApiError(401, t('auth.sessionExpired'));
    }
  }

  if (!response.ok) {
    throw await toApiError(response);
  }

  return response;
}

/**
 * Tiplangan so'rov — javob JSON deb talqin qilinadi.
 */
export async function apiFetch<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await requestWithRefresh(path, options);

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  if (text === '') return undefined as T;

  return JSON.parse(text) as T;
}

/**
 * Xuddi shu so'rov, lekin javob MATN sifatida qaytariladi.
 *
 * Chop etish endpointlari (`/api/print/*`) tayyor HTML beradi va uni
 * `JSON.parse()` ga berish `SyntaxError` ga olib borardi — brauzerdagi
 * xato esa serverdan kelgan javob to'g'ri bo'lgani uchun butunlay boshqa
 * joyda qidirilardi.
 */
export async function apiText(path: string, options: RequestOptions = {}): Promise<string> {
  const response = await requestWithRefresh(path, options);
  return response.status === 204 ? '' : response.text();
}

/**
 * XOM javob — JSON emas.
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ NEGA `fetch` NI QO'LDA CHAQIRMAYMIZ                                      │
 * │                                                                          │
 * │ Ovoz endpointi audio qaytaradi va `apiFetch` uni JSON deb o'qishga       │
 * │ urinardi. Vasvasa — chaqiruvchida oddiy `fetch` yozish. Rad etildi:      │
 * │ o'shanda token qo'shish va MUDDATI TUGAGANDA yangilash mantig'i          │
 * │ takrorlanardi, va u ikkinchi nusxada albatta soddalashtirilardi —        │
 * │ ya'ni ovoz tugmasi sessiya eskirganda jimgina 401 berardi.               │
 * │                                                                          │
 * │ `requestWithRefresh` esa aynan shu ishni qiladi. Bu yerda faqat javob    │
 * │ O'QILMAYDI: uni chaqiruvchi o'zi (blob, stream) hal qiladi.              │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
export function apiRaw(path: string, options: RequestOptions = {}): Promise<Response> {
  return requestWithRefresh(path, options);
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'GET' }),

  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'POST', body }),

  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'PATCH', body }),

  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> =>
    apiFetch<T>(path, { ...options, method: 'DELETE' }),
};
